import { useRef, useState } from "react";
import { useContactModal } from "@/core/providers/ContactModalContext";
import { useContent } from "@/shared/lib/i18n";

const CHANNEL_IMAGES: Record<string, string> = {
  influencers: "/illustrations/peoples/influencers.webp",
  performance: "/illustrations/peoples/perfomance.webp",
  ppc: "/illustrations/peoples/ppc.webp",
  seo: "/illustrations/peoples/seo.webp",
};

export function FindPartners() {
  const { openModal } = useContactModal();
  const { findPartners } = useContent();
  const [activeIndex, setActiveIndex] = useState(0);
  const [tappedKey, setTappedKey] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const titleLines = findPartners.title.split("\n");

  function handleScroll() {
    const el = scrollRef.current;
    if (!el) return;
    const index = Math.round(el.scrollLeft / el.offsetWidth);
    setActiveIndex(index);
  }

  return (
    <section className="py-16 lg:py-24 lg:px-8" id="partners">
      <div className="mx-auto max-w-[1760px]">
        {/* Heading */}
        <div className="text-center mb-10 lg:mb-16 px-[14px] lg:px-0">
          <h2 className="font-display text-[36px] lg:text-[52px] leading-tight text-white mb-4">
            {titleLines[0]}
            <br />
            {titleLines[1]}
          </h2>
          <p className="text-[15px]">{findPartners.subtitle}</p>
        </div>

        {/* Desktop grid */}
        <div className="hidden lg:grid grid-cols-4 gap-4">
          {findPartners.channels.map((ch) => (
            <div key={ch.key} className="group relative rounded-2xl overflow-hidden h-[600px] cursor-pointer">
              <img
                src={CHANNEL_IMAGES[ch.key] ?? ""}
                alt={ch.title}
                className="absolute inset-0 w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 z-20 rounded-b-2xl">
                <div className="px-6 pt-8 pb-4 transition-transform duration-300 group-hover:-translate-y-1">
                  <p className="text-[#f4a425] text-base font-bold mb-2">{ch.title}</p>
                  <p className="text-[13px] text-white leading-relaxed max-w-[372px]">{ch.description}</p>
                </div>
                <div className="px-6 pb-6 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 delay-150">
                  <button
                    onClick={openModal}
                    className="flex items-center justify-center w-full h-10 rounded-full text-sm font-semibold text-white border border-white/40 hover:border-white/70 transition-colors"
                  >
                    {findPartners.cta}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile slider */}
        <div className="lg:hidden">
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {findPartners.channels.map((ch) => (
              <div
                key={ch.key}
                className="snap-center shrink-0 w-screen px-[14px]"
                onClick={() => setTappedKey((v) => (v === ch.key ? null : ch.key))}
              >
                <div className="relative rounded-2xl overflow-hidden" style={{ height: "80vw", minHeight: 360 }}>
                  <img
                    src={CHANNEL_IMAGES[ch.key] ?? ""}
                    alt={ch.title}
                    className={`absolute inset-0 w-full h-full object-cover object-top transition-all duration-500 ${tappedKey === ch.key ? "grayscale-0" : "grayscale"}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                  <div
                    className="absolute bottom-0 inset-x-0 z-20 transition-transform duration-300 ease-out"
                    style={{ transform: tappedKey === ch.key ? "translateY(0)" : "translateY(60px)" }}
                  >
                    <div className="px-5 pt-8 pb-4">
                      <p className="text-[#f4a425] text-base font-bold mb-2">{ch.title}</p>
                      <p className="text-[13px] text-white leading-relaxed">{ch.description}</p>
                    </div>
                    <div className="px-5 pb-5">
                      <button
                        className="flex items-center justify-center w-full h-10 rounded-full text-sm font-semibold text-white border border-white/40"
                        onClick={(e) => { e.stopPropagation(); openModal(); }}
                      >
                        {findPartners.cta}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mt-6">
            {findPartners.channels.map((_, i) => (
              <div
                key={i}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === activeIndex ? 28 : 12,
                  height: 12,
                  background: i === activeIndex ? "#fec201" : "rgba(255,255,255,0.2)",
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
